import { isEmpty } from 'lodash';
import { useContext, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { MBButton } from '@shared/components/MBButton';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBIconV2, MBImage } from '@shared/components/image';
import { MBTextSubtitle } from '@shared/components/typography';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { ColorThemeContext, Colors, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { SignatureClearButton } from '../SignatureClearButton';
import { SignatureProps } from '../SignatureProps';

export function WebSignatureView({
  block,
  onSignatureUpdate,
  signatureUrl,
  height,
  marginHorizontal,
}: SignatureProps) {
  const ref = useRef<SignatureCanvas>(null);
  const responsiveForms = useResponsiveFormContext();
  const colors = useContext(ColorThemeContext);
  const [currentDataUrl, setCurrentDataUrl] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);

  function onBegin() {
    setIsDrawing(true);
  }

  function onEnd() {
    setIsDrawing(false);
    setCurrentDataUrl(ref.current?.toDataURL());
  }

  const clear = () => {
    ref.current?.clear();
    setCurrentDataUrl('');
    onSignatureUpdate('');
  };

  const onSave = () => {
    onSignatureUpdate(ref.current?.toDataURL());
    setCurrentDataUrl('');
    ref.current?.clear();
  };

  const isReadOnly = block?.readonly;
  const hasUrl = !isEmpty(currentDataUrl) || !isEmpty(signatureUrl);
  const showCornerButton = hasUrl && !block?.readonly && !isReadOnly;
  const showPlaceholder = !isDrawing && isEmpty(currentDataUrl) && isEmpty(signatureUrl);
  const showCanvas = !isReadOnly && isEmpty(signatureUrl);
  const showImage = !isEmpty(signatureUrl);
  const showSaveButton = !isReadOnly && !isEmpty(currentDataUrl);

  return (
    <View>
      <MBView
        flex
        style={[
          {
            marginHorizontal,
            borderRadius: 8,
            borderStyle: isEmpty(currentDataUrl) ? 'dashed' : 'solid',
            borderWidth: 2,
            height,
            backgroundColor: colors.input.backgroundInactive,
            borderColor: isEmpty(currentDataUrl)
              ? colors.input.borderInactive
              : colors.input.borderActive,
          },
          responsiveForms && {
            borderWidth: 0,
            backgroundColor: Colors.v2.transparent,
            borderColor: Colors.v2.transparent,
          },
        ]}
      >
        {showCornerButton && <SignatureClearButton onPress={clear} />}
        {showPlaceholder && (
          <View
            center
            row
            paddingTop={2}
            style={{
              position: 'absolute',
              alignSelf: 'center',
              zIndex: 10,
              pointerEvents: 'none',
            }}
          >
            <MBIconV2
              color={colors.foregroundActive}
              size={grid(5)}
              name={block?.icon || 'signature-ballpoint-pen'}
            />
            <View paddingLeft={1} paddingRight={3}>
              <MBTextSubtitle color={colors.foregroundActive}>{block?.placeholder}</MBTextSubtitle>
            </View>
          </View>
        )}
        {showCanvas && (
          <>
            <SignatureCanvas
              ref={ref}
              canvasProps={{ style: { height } }}
              onBegin={onBegin}
              onEnd={onEnd}
              penColor={colors.foregroundActive}
              backgroundColor={'transparent'}
            />
            <View
              marginTop={7}
              style={{
                position: 'absolute',
                alignSelf: 'center',
                width: '75%',
                height: 2,
                backgroundColor: colors.foregroundActive,
                zIndex: 0,
              }}
            />
          </>
        )}
        {showImage && (
          <View style={{ height }}>
            <MBImage source={{ uri: signatureUrl }} fill resizeMode="contain" />
          </View>
        )}
      </MBView>
      {showSaveButton && (
        <View style={{ alignSelf: 'flex-end' }} margin={1}>
          <MBButton
            style={{ alignSelf: 'flex-end' }}
            size="small"
            onPress={onSave}
            title={i18n('common.save')}
          />
        </View>
      )}
    </View>
  );
}
